import { BookOpen, LogOut, Settings, LayoutDashboard, User, Users, LineChart, CheckSquare, Building2, Shield, Calculator } from "lucide-react";
import { CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "@/lib/auth";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { realtimeManager } from "@/lib/realtime";
import { preloadRoutes } from "@/lib/utils";
import SubscriptionStatus from "@/components/stripe/SubscriptionStatus";

interface SidebarProps {
  className?: string;
}

export default function Sidebar({ className }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [propertyCount, setPropertyCount] = useState<number>(0);
  const [kundeId, setKundeId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('kunde');

  useEffect(() => {
    const fetchCustomerId = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: customerData, error: customerError } = await supabase
          .from('kunden')
          .select('id, rolle')
          .eq('auth_user_id', user.id)
          .single();

        if (customerError) throw customerError;
        if (!customerData) return;

        setKundeId(customerData.id);
        setUserRole(customerData.rolle || 'kunde');
        
        if (customerData.rolle === 'kunde') {
          fetchPropertyCount(customerData.id);
        }
      } catch (error) {
        console.error('Error fetching customer ID:', error);
      }
    };

    fetchCustomerId();
  }, []);

  useEffect(() => {
    if (!kundeId || userRole !== 'kunde') return;

    // Set up real-time subscription for property count updates
    const unsubscribe = realtimeManager.subscribeToProperties(
      kundeId,
      () => {
        // Refresh the count when any property change occurs
        fetchPropertyCount(kundeId);
      }
    );

    return unsubscribe;
  }, [kundeId, userRole]);

  const fetchPropertyCount = async (customerId: string) => {
    try {
      const { count, error: propertiesError } = await supabase
        .from('immobilien')
        .select('*', { count: 'exact' })
        .eq('kunde_id', customerId);

      if (propertiesError) throw propertiesError;
      setPropertyCount(count || 0);
    } catch (error) {
      console.error('Error fetching property count:', error);
    }
  };

  const handleSignOut = async () => {
    // Clean up real-time subscriptions
    realtimeManager.cleanup();
    await signOut();
    navigate('/login');
  };

  // Preload function for routes
  const handlePreload = (path: string) => {
    switch (path) {
      case '/optimierung':
        preloadRoutes.overview();
        break;
      case '/performance':
        preloadRoutes.development();
        break;
      case '/wertentwicklung':
        preloadRoutes.todos();
        break;
      case '/portfoliopage':
        preloadRoutes.dashboard();
        break;
      case '/akademie':
        preloadRoutes.academy();
        break;
      case '/community':
        preloadRoutes.community();
        break;
      case '/profil':
        preloadRoutes.profile();
        break;
      case '/admin':
        preloadRoutes.adminDashboard();
        break;
      case '/admin/customers':
      case '/admin/properties':
        preloadRoutes.adminPage();
        break;
      case '/admin/debug':
        preloadRoutes.adminDebug();
        break;
      case '/admin/analytics':
      case '/admin/settings':
        preloadRoutes.adminDashboard();
        break;
    }
  };

  // Different menu items based on user role
  const getMenuItems = () => {
    if (userRole === 'admin') {
      return [
        { icon: Shield, label: "Admin Dashboard", path: "/admin", disabled: false },
        { icon: Users, label: "Kunden", path: "/admin/customers", disabled: false },
        { icon: Building2, label: "Immobilien", path: "/admin/properties", disabled: false },
        { icon: CheckSquare, label: "Tasks", path: "/admin/tasks", disabled: false },
        { icon: LineChart, label: "Analytics", path: "/admin/analytics", disabled: false },
        { icon: Settings, label: "Einstellungen", path: "/admin/settings", disabled: false },
      ];
    }

    // Default customer menu items
    return [
      { icon: LayoutDashboard, label: "Übersicht", path: "/optimierung", disabled: false },
      { icon: LineChart, label: "Entwicklung", path: "/performance", disabled: false },
      { icon: CheckSquare, label: "To-Do's", path: "/wertentwicklung", disabled: false },
      { icon: Building2, label: "Portfolio", path: "/portfoliopage", disabled: false },
      { icon: Calculator, label: "Steuersimulation", path: "/steuersimulation", disabled: false },
      { icon: BookOpen, label: "Akademie", path: "/akademie", disabled: false },
      { icon: Users, label: "Community", path: "/community", disabled: false },
      { icon: CreditCard, label: "Premium", path: "/products", disabled: false },
      { icon: User, label: "Profil", path: "/profil", disabled: false },
      { icon: Settings, label: "Einstellungen", path: "/einstellungen", disabled: false },
    ];
  };

  const menuItems = getMenuItems();

  return (
    <TooltipProvider>
      <div className={cn("flex flex-col h-screen border-r border-neutral-200/50 bg-white/60 backdrop-blur-xl", className)}>
        <div className="p-4 border-b border-neutral-200/50">
          <Logo />
        </div>
        
        <nav className="flex-1 p-4">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              
              if (item.disabled) {
                return (
                  <Tooltip key={item.label}>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "flex items-center w-full px-3 py-2 text-sm rounded-lg transition-colors cursor-not-allowed opacity-50",
                          "text-neutral-400"
                        )}
                      >
                        <item.icon className="h-4 w-4 mr-3 text-neutral-400" />
                        {item.label}
                        {item.label === "Portfolio" && propertyCount > 0 && (
                          <span className="ml-auto text-xs bg-neutral-300 text-neutral-500 px-2 py-0.5 rounded-full">
                            {propertyCount}
                          </span>
                        )}
                        {item.label === "Akademie" && (
                          <span className="ml-auto text-xs bg-neutral-300 text-neutral-500 px-2 py-0.5 rounded-full">Neu</span>
                        )}
                        {item.label === "Community" && (
                          <span className="ml-auto text-xs bg-neutral-300 text-neutral-500 px-2 py-0.5 rounded-full">Neu</span>
                        )}
                        {item.label === "Premium" && userRole === 'kunde' && (
                          <span className="ml-auto text-xs bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-2 py-0.5 rounded-full">Pro</span>
                        )}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="bg-neutral-800 text-white border-neutral-700">
                      <p>Ab 2 Analysen zur Verfügung</p>
                    </TooltipContent>
                  </Tooltip>
                );
              }

              return (
                <Link
                  key={item.label}
                  to={item.path}
                  data-portfolio-nav={item.label === "Portfolio" ? "true" : undefined}
                  onMouseEnter={() => handlePreload(item.path)}
                  className={cn(
                    "flex items-center w-full px-3 py-2 text-sm rounded-lg transition-colors",
                    isActive
                      ? "bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-600"
                      : "text-neutral-600 hover:bg-gradient-to-r hover:from-blue-500/5 hover:to-purple-500/5 hover:text-blue-600"
                  )}
                >
                  <item.icon className={cn(
                    "h-4 w-4 mr-3",
                    isActive && "text-blue-500"
                  )} />
                  {item.label}
                  {item.label === "Portfolio" && propertyCount > 0 && userRole === 'kunde' && (
                    <span className="ml-auto text-xs bg-gradient-to-r from-blue-500 to-purple-500 text-white px-2 py-0.5 rounded-full">
                      {propertyCount}
                    </span>
                  )}
                  {item.label === "Akademie" && userRole === 'kunde' && (
                    <span className="ml-auto text-xs bg-gradient-to-r from-blue-400 to-purple-400 text-white px-2 py-0.5 rounded-full">Neu</span>
                  )}
                  {item.label === "Community" && userRole === 'kunde' && (
                    <span className="ml-auto text-xs bg-gradient-to-r from-blue-400 to-purple-400 text-white px-2 py-0.5 rounded-full">Neu</span>
                  )}
                  {item.label === "Steuersimulation" && userRole === 'kunde' && (
                    <span className="ml-auto text-xs bg-gradient-to-r from-green-400 to-emerald-400 text-white px-2 py-0.5 rounded-full">Neu</span>
                  )}
                  {item.label === "Premium" && userRole === 'kunde' && (
                    <span className="ml-auto text-xs bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-2 py-0.5 rounded-full">Pro</span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Subscription Status */}
          {userRole === 'kunde' && (
            <div className="mt-6 px-3">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">
                Ihr Plan
              </div>
              <SubscriptionStatus />
            </div>
          )}

          {userRole === 'kunde' && (
            <div className="mt-8">
              <div className="text-xs font-medium text-neutral-400 uppercase tracking-wider mb-3">
                Aktuelle Kurse
              </div>
              <div className="space-y-2">
                {[
                  "Immobilienbewertung Grundlagen",
                  "Portfoliooptimierung",
                  "Steuerliche Aspekte"
                ].map((course) => (
                  <div
                    key={course}
                    className="text-sm text-neutral-600 px-3 py-2 hover:bg-gradient-to-r hover:from-blue-500/5 hover:to-purple-500/5 hover:text-blue-600 rounded-lg cursor-pointer transition-colors"
                    onMouseEnter={() => preloadRoutes.academy()}
                  >
                    {course}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleSignOut}
            className="flex items-center w-full px-3 py-2 mt-8 text-sm rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Abmelden
          </button>
        </nav>
      </div>
    </TooltipProvider>
  );
}